import React from 'react';
import { CircularProgress, Tooltip } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import format from 'date-fns/format';
import { ResultsReportRow } from 'api/bitedb/resultsReport';

interface GeneratedReportsRowProps {
  report: ResultsReportRow;
  onDownload: (row: ResultsReportRow) => void;
  isDownloading: boolean;
}

const formatDateLabel = (value?: string) => {
  if (!value) {
    return 'Not available';
  }

  try {
    return format(new Date(value), 'MMM d, yyyy p');
  } catch {
    return 'Not available';
  }
};

const getStatusBadgeClass = (status: ResultsReportRow['status']) => {
  if (status === 'completed') {
    return 'low';
  }

  if (status === 'failed') {
    return 'high';
  }

  return 'medium';
};

const getStatusLabel = (status: ResultsReportRow['status']) => {
  if (!status) {
    return 'Pending';
  }

  return `${status.charAt(0).toUpperCase()}${status.slice(1)}`;
};

const getDisplayFileName = (report: ResultsReportRow) => {
  if (report.fileName) {
    return report.fileName;
  }

  const datePrefix = (report.generatedAt || report.createdAt || '').slice(0, 10);
  return `results_report_${datePrefix || 'unknown'}.pdf`;
};

const GeneratedReportsRow: React.FC<GeneratedReportsRowProps> = ({
  report,
  onDownload,
  isDownloading,
}) => {
  const canDownload = report.status === 'completed';

  return (
    <div className='screening-results-row'>
      <div
        className='screening-results-row-default-cell'
        style={{ flex: '1.8', minWidth: '280px' }}
      >
        <div className='screening-results-row-default-cell-inner'>
          <div
            className='screening-results-row-default-cell-text'
            style={{ fontWeight: 600 }}
          >
            {getDisplayFileName(report)}
          </div>
        </div>
      </div>

      <div className='screening-results-row-default-cell' style={{ maxWidth: '160px' }}>
        <div className='screening-results-row-default-cell-inner'>
          <div className='screening-results-row-default-cell-text'>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span className={`screening-badge ${getStatusBadgeClass(report.status)}`}>
                {getStatusLabel(report.status)}
              </span>
              {report.status === 'failed' && report.errorMessage ? (
                <Tooltip title={report.errorMessage} arrow placement='top'>
                  <ErrorOutlineIcon sx={{ fontSize: 14, color: '#B42318' }} />
                </Tooltip>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <div className='screening-results-row-default-cell' style={{ maxWidth: '200px' }}>
        <div className='screening-results-row-default-cell-inner'>
          <div className='screening-results-row-default-cell-text'>
            {formatDateLabel(report.createdAt)}
          </div>
        </div>
      </div>

      <div className='screening-results-row-default-cell' style={{ maxWidth: '220px' }}>
        <div className='screening-results-row-default-cell-inner'>
          <div className='screening-results-row-default-cell-text'>
            {report.createdByName || 'Unknown User'}
          </div>
        </div>
      </div>

      <div
        className='screening-results-row-default-cell'
        style={{ maxWidth: '120px', justifyContent: 'center' }}
      >
        <div className='screening-results-row-default-cell-inner'>
          <div className='screening-results-row-default-cell-text'>
            {typeof report.entityCount === 'number' ? report.entityCount : '-'}
          </div>
        </div>
      </div>

      <div
        className='screening-results-row-default-cell'
        style={{ maxWidth: '160px', justifyContent: 'center' }}
      >
        <div className='screening-results-row-default-cell-inner'>
          <button
            className='screening-btn'
            disabled={!canDownload || isDownloading}
            onClick={() => onDownload(report)}
            style={{
              width: '100%',
              minWidth: 120,
              opacity: !canDownload || isDownloading ? 0.6 : 1,
              cursor: !canDownload || isDownloading ? 'not-allowed' : 'pointer',
            }}
          >
            {isDownloading ? (
              <CircularProgress size={16} color='inherit' />
            ) : (
              <>
                <FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />
                <span style={{ marginLeft: 6 }}>Download</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GeneratedReportsRow;
